import { driver } from "driver.js";
import "driver.js/dist/driver.css";
import api from "@/lib/api";

// Guided in-app walkthroughs (driver.js). Steps anchor to sidebar nav test-ids
// (present on every screen), so tours run from anywhere and degrade gracefully.
export const TOURS = [
  {
    id: "welcome",
    title: "Getting started",
    desc: "A 30-second tour of your workspace",
    steps: [
      { popover: { title: "Welcome to Lottie 👋", description: "Here's a quick tour of your workspace. Use the arrow keys or the buttons to move through it." } },
      { element: '[data-testid="sidebar-nav-expanded"]', popover: { title: "Your navigation", description: "Everything lives in this sidebar — registers, reports and settings.", side: "right", align: "start" } },
      { element: '[data-testid="nav-accruals"]', popover: { title: "Registers", description: "Your live Accruals, Prepayments and Deferred Income registers, always in sync with Xero.", side: "right" } },
      { element: '[data-testid="nav-close"]', popover: { title: "Month-end close", description: "Run your close checklist and post the period's journals from here.", side: "right" } },
      { element: '[data-testid="nav-reports"]', popover: { title: "Reports", description: "Export branded PDF and Excel reports for every register.", side: "right" } },
      { element: '[data-testid="nav-help"]', popover: { title: "Help is always here", description: "Guides, FAQs and these tours live under Help. That's it — enjoy!", side: "right" } },
    ],
  },
  {
    id: "connect-xero",
    title: "Connect Xero",
    desc: "Link your Xero organisation",
    steps: [
      { popover: { title: "Connect your Xero", description: "Lottie reads your invoices and bills straight from Xero. Here's where to link it." } },
      { element: '[data-testid="nav-settings"]', popover: { title: "Open Entity Settings", description: "Click here, then choose the “Connect to Xero” tab.", side: "right" } },
      { popover: { title: "Authorise in Xero", description: "Click “Connect to Xero”, sign in to Xero, pick your organisation(s) and allow access. Your chart of accounts imports automatically." } },
    ],
  },
  {
    id: "create-accrual",
    title: "Create an accrual",
    desc: "Add your first accrual",
    steps: [
      { popover: { title: "Create an accrual", description: "There are two easy ways to add accruals — let's look at both." } },
      { element: '[data-testid="nav-recurring-accruals"]', popover: { title: "Recurring accruals", description: "Lottie spots regular supplier bills and suggests month-end accruals you can post in one click.", side: "right" } },
      { element: '[data-testid="nav-unscheduled"]', popover: { title: "Or do it manually", description: "Open Unscheduled Items to create an accrual, prepayment or deferred-income schedule by hand.", side: "right" } },
      { element: '[data-testid="nav-accruals"]', popover: { title: "See it in the register", description: "Once posted, every accrual appears here with its release schedule and journals.", side: "right" } },
    ],
  },
];

export function tourById(id) {
  return TOURS.find((t) => t.id === id) || null;
}

function recordTourComplete(id) {
  api.post("/learning/progress", { kind: "tour", key: id }).catch(() => {});
}

// Start a tour. Records completion (LMS) when the user reaches the final step,
// and calls opts.onComplete so callers can refresh progress.
export function startTour(id, opts = {}) {
  const tour = TOURS.find((t) => t.id === id);
  if (!tour) return;
  const steps = tour.steps.filter((s) => !s.element || document.querySelector(s.element));
  if (!steps.length) return;

  const lastIndex = steps.length - 1;
  let reachedEnd = false;

  const d = driver({
    showProgress: true,
    allowClose: true,
    nextBtnText: "Next",
    prevBtnText: "Back",
    doneBtnText: "Done",
    popoverClass: "lottie-tour",
    steps,
    onHighlightStarted: () => {
      try {
        if (d.getActiveIndex && d.getActiveIndex() === lastIndex) reachedEnd = true;
      } catch { /* noop */ }
    },
    onDestroyed: () => {
      if (reachedEnd) {
        recordTourComplete(id);
        opts.onComplete?.(id);
      }
    },
  });
  d.drive();
}
